import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Trash2, Plus, ShieldCheck, Database, ServerCog, Loader2, Mail } from "lucide-react";
import API from "../lib/api";
import AdminGuard from "../components/admin/AdminGuard";
import AdminShell from "../components/admin/AdminShell";
import { errorMessage } from "../lib/errors";
import { useAuth } from "../lib/auth";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [envList, setEnvList] = useState([]);
  const [envVarName, setEnvVarName] = useState("ADMIN_EMAILS");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [form, setForm] = useState({ email: "", notes: "" });

  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = () => setRefreshTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await API.get("/admin/users");
        if (cancelled) return;
        setItems(data.items || []);
        setEnvList(data.env_allowlist || []);
        setEnvVarName(data.env_var_name || "ADMIN_EMAILS");
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e, t("admin.users.load_failed", "Couldn\u2019t load admin users")));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick, t]);

  const handlePromote = async (e) => {
    e.preventDefault();
    const email = (form.email || "").trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      toast.error(t("admin.users.email_invalid", "Enter a valid email"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post("/admin/users", { email, notes: form.notes || null });
      if (data?.already_existed) {
        toast.info(t("admin.users.promote_existed", "{{email}} was already an admin", { email }));
      } else {
        toast.success(t("admin.users.promote_success", "{{email}} promoted to admin", { email }));
      }
      setForm({ email: "", notes: "" });
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, t("admin.users.promote_failed", "Couldn\u2019t promote user")));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDemote = async (email) => {
    if (!window.confirm(t("admin.users.confirm_demote", "Remove admin access from {{email}}?", { email }))) return;
    setRemovingEmail(email);
    try {
      const { data } = await API.delete(`/admin/users/${encodeURIComponent(email)}`);
      if (data?.still_in_env) {
        toast.info(t("admin.users.demote_env_still", "Removed from DB. {{email}} is still in {{var}}.", { email, var: envVarName }));
      } else {
        toast.success(t("admin.users.demote_success", "{{email}} demoted", { email }));
      }
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, t("admin.users.demote_failed", "Couldn\u2019t demote user")));
    } finally {
      setRemovingEmail(null);
    }
  };

  return (
    <AdminGuard>
      <AdminShell active="users">
        <div className="space-y-8" data-testid="admin-users-page">
          <div>
            <div className="eyebrow mb-2">{t("admin.users.eyebrow", "\u2014 Access Control")}</div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="admin-users-title">
              {t("admin.users.title", "Admin Users")}
            </h2>
            <p className="text-sm text-white/65 mt-2 max-w-2xl leading-relaxed">
              {t("admin.users.subtitle", "Manage who can access the admin console. The ADMIN_EMAILS environment variable always grants access; this list is for day-to-day management.")}
            </p>
          </div>

          {/* ENV allowlist (read-only) */}
          <div className="card-surface p-5" data-testid="env-allowlist">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center shrink-0">
                <ServerCog className="w-4 h-4 gold-text" />
              </div>
              <div className="flex-1">
                <div className="font-display text-base">{t("admin.users.env_title", "Environment allowlist")}</div>
                <p className="text-sm text-white/65 mt-1 leading-relaxed">
                  {t("admin.users.env_sub", "Configured via the {{var}} environment variable. Read-only here \u2014 update via the secrets dashboard.", { var: envVarName })}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {envList.length === 0 ? (
                    <span className="text-xs text-white/50" data-testid="env-empty">{t("admin.users.env_empty", "Empty")}</span>
                  ) : (
                    envList.map((email) => (
                      <span key={email} className="text-xs px-2.5 py-1 rounded-full border border-[#C8A24A]/40 bg-[#C8A24A]/10 gold-text" data-testid={`env-chip-${email}`}>
                        {email}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Promote form */}
          <form className="card-surface p-5" onSubmit={handlePromote} data-testid="promote-form">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/35 to-[#7A5F2A]/15 border border-[#C8A24A]/40 grid place-items-center shrink-0">
                <Plus className="w-4 h-4 gold-text" />
              </div>
              <div>
                <div className="font-display text-base">{t("admin.users.add_title", "Promote an admin")}</div>
                <p className="text-xs text-white/55 mt-0.5">
                  {t("admin.users.add_sub", "Adds the user to the DB allowlist and updates their account if they\u2019re registered.")}
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3">
              <div>
                <label className="text-xs text-white/55 mb-1 block" htmlFor="promote-email">{t("admin.users.email_label", "Email")}</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  <input
                    id="promote-email"
                    type="email"
                    required
                    autoComplete="off"
                    placeholder={t("admin.users.email_placeholder", "user@example.com")}
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full bg-[#0F1116] border border-white/10 rounded-lg ps-9 pe-4 py-3 text-sm focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                    data-testid="promote-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-white/55 mb-1 block" htmlFor="promote-notes">{t("admin.users.notes_label", "Notes")}</label>
                <input
                  id="promote-notes"
                  type="text"
                  autoComplete="off"
                  placeholder={t("admin.users.notes_placeholder", "Notes (optional)")}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                  data-testid="promote-notes-input"
                />
              </div>
              <div className="flex md:items-end">
                <button type="submit" disabled={submitting} className="btn-primary w-full md:w-auto text-sm whitespace-nowrap inline-flex items-center justify-center gap-2" data-testid="promote-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {t("admin.users.add_btn", "Promote")}
                </button>
              </div>
            </div>
          </form>

          {/* DB allowlist */}
          <div className="card-surface p-5" data-testid="db-allowlist">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/35 to-[#7A5F2A]/15 border border-[#C8A24A]/40 grid place-items-center shrink-0">
                  <Database className="w-4 h-4 gold-text" />
                </div>
                <div>
                  <div className="font-display text-base">{t("admin.users.db_title", "Database allowlist")}</div>
                  <p className="text-xs text-white/55 mt-0.5">{t("admin.users.db_sub", "Day-to-day admins managed via this dashboard.")}</p>
                </div>
              </div>
              <span className="text-xs text-white/55" data-testid="db-count">{items.length} {t("admin.users.count_label", "admin(s)")}</span>
            </div>

            {loading ? (
              <div className="text-sm text-white/60 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{t("admin.loading", "Loading\u2026")}</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-white/60" data-testid="db-empty">{t("admin.users.empty", "No admin users in the database yet.")}</div>
            ) : (
              <ul className="divide-y divide-white/5" data-testid="db-list">
                {items.map((it) => {
                  const isSelf = (user?.email || "").toLowerCase() === (it.email || "").toLowerCase();
                  const isAlsoEnv = envList.map((e) => (e || "").toLowerCase()).includes((it.email || "").toLowerCase());
                  return (
                    <li key={it.email} className="py-3 flex items-center justify-between gap-4" data-testid={`db-row-${it.email}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <ShieldCheck className="w-4 h-4 gold-text shrink-0" />
                          <span className="text-sm font-medium truncate">{it.email}</span>
                          {isSelf && <span className="text-[10px] uppercase tracking-wider gold-text border border-[#C8A24A]/40 rounded px-1.5 py-0.5">{t("admin.users.self_chip", "You")}</span>}
                          {isAlsoEnv && <span className="text-[10px] uppercase tracking-wider gold-text border border-[#C8A24A]/40 rounded px-1.5 py-0.5">{t("admin.users.also_env", "Also in ENV")}</span>}
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          {it.added_by ? t("admin.users.added_by", "Added by {{email}}", { email: it.added_by }) : "—"} · {(it.added_at || "").replace("T", " ").slice(0, 19)}
                          {it.notes ? <span className="ms-1 text-white/65">— {it.notes}</span> : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDemote(it.email)}
                        disabled={removingEmail === it.email}
                        className="btn-ghost text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1.5"
                        data-testid={`demote-${it.email}`}
                      >
                        {removingEmail === it.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        {t("admin.users.remove", "Demote")}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </AdminShell>
    </AdminGuard>
  );
}
